import { Icons } from '../utils/Icons.js';

export default class OnboardingManager {
    constructor(gameEngine) {
        this.engine = gameEngine;
        this.step = 1;
        this.isActive = false;
        this.container = null;
        this.halo = null;
    }

    init() {
        this.attachListeners();
        const version = localStorage.getItem('pixelblob_onboarding_version') || localStorage.getItem('quicky_onboarding_version') || '1';
        if (version !== '3') {
            localStorage.removeItem('pixelblob_onboarding_done');
            localStorage.removeItem('quicky_onboarding_done');
            localStorage.setItem('pixelblob_onboarding_version', '3');
        }

        const isDone = localStorage.getItem('pixelblob_onboarding_done') || localStorage.getItem('quicky_onboarding_done');
        if (isDone === 'true') return;
    }

    start(forced = false) {
        if (this.isActive) return;
        if (!forced && (localStorage.getItem('pixelblob_onboarding_done') === 'true' || localStorage.getItem('quicky_onboarding_done') === 'true')) return;

        this.isActive = true;
        this.createOverlay();
        this.showStep(1);
    }

    createOverlay() {
        this.container = document.createElement('div');
        this.container.id = 'onboarding-overlay';
        this.container.style.position = 'fixed';
        this.container.style.top = '0';
        this.container.style.left = '0';
        this.container.style.width = '100%';
        this.container.style.height = '100%';
        this.container.style.background = 'rgba(0, 0, 0, 0.85)';
        this.container.style.backdropFilter = 'blur(8px)';
        this.container.style.zIndex = '10000';
        this.container.style.display = 'flex';
        this.container.style.flexDirection = 'column';
        this.container.style.alignItems = 'center';
        this.container.style.justifyContent = 'center';
        this.container.style.color = '#fff';
        this.container.style.padding = '40px 20px';
        this.container.style.textAlign = 'center';
        this.container.style.transition = 'opacity 0.5s ease, -webkit-mask-image 0.5s ease, justify-content 0.5s ease';
        this.container.style.webkitMaskRepeat = 'no-repeat';
        this.container.style.webkitMaskSize = '100% 100%';

        document.body.appendChild(this.container);
    }

    removeHalo() {
        if (this.halo) {
            this.halo.remove();
            this.halo = null;
        }
        if (this.container) {
            this.container.style.webkitMaskImage = 'none';
            this.container.style.justifyContent = 'center';
        }
    }

    applySpotlight(rect, size) {
        if (!this.container) return;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const radius = size / 2;

        // Punch a hole in the overlay using a radial gradient mask
        // Everything inside 'radius' is transparent, everything outside is black (visible)
        this.container.style.webkitMaskImage = `radial-gradient(circle ${radius}px at ${centerX}px ${centerY}px, transparent 100%, black 100%)`;
    }

    repositionContent(rect) {
        if (!this.container) return;
        
        const screenHeight = window.innerHeight;
        const targetCenterY = rect.top + rect.height / 2;

        // If target is in the bottom half, push content to the top
        if (targetCenterY > screenHeight / 2) {
            this.container.style.justifyContent = 'flex-start';
        } 
        // If target is in the top half, push content to the bottom
        else {
            this.container.style.justifyContent = 'flex-end';
        }
    }

    setFocusHalo(selector) {
        this.removeHalo();
        const el = document.getElementById(selector);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        
        // Dynamic positioning of the overlay content to avoid overlap
        this.repositionContent(rect);

        this.halo = document.createElement('div');
        this.halo.className = 'tutorial-focus-halo';

        // Size it slightly larger than the target
        const size = Math.max(rect.width, rect.height) + 20;
        this.halo.style.width = `${size}px`;
        this.halo.style.height = `${size}px`;
        this.halo.style.top = `${rect.top + rect.height / 2 - size / 2}px`;
        this.halo.style.left = `${rect.left + rect.width / 2 - size / 2}px`;

        document.body.appendChild(this.halo);
        this.applySpotlight(rect, size);
    }

    showStep(step) {
        const t = this.engine.i18n.t.bind(this.engine.i18n);
        this.container.innerHTML = '';
        this.removeHalo();
        this.step = step;

        const content = document.createElement('div');
        content.classList.add('onboarding-content');
        content.style.maxWidth = '400px';
        content.style.animation = 'scaleIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';

        const title = document.createElement('h2');
        title.style.color = 'var(--primary-color)';
        title.style.fontSize = '2rem';
        title.style.marginBottom = '20px';
        title.style.textShadow = '0 0 10px var(--primary-color)';

        const text = document.createElement('p');
        text.style.fontSize = '1.2rem';
        text.style.lineHeight = '1.6';
        text.style.marginBottom = '30px';

        const btn = document.createElement('button');
        btn.classList.add('neon-btn');
        btn.textContent = t('common.next');
        btn.onclick = () => this.next();

        content.appendChild(title);
        content.appendChild(text);
        content.appendChild(btn);

        if (step === 1) {
            title.textContent = t('onboarding.step1Title');
            text.innerHTML = t('onboarding.step1Text');
        } else if (step === 2) {
            title.textContent = t('onboarding.step2Title');
            text.innerHTML = t('onboarding.step2Text');
        } else if (step === 3) {
            title.textContent = t('onboarding.step3Title');
            text.innerHTML = t('onboarding.step3Text');
        } else if (step === 4) {
            title.textContent = t('onboarding.step4Title');
            text.innerHTML = t('onboarding.step4Text');
            this.setFocusHalo('start-btn');
        } else if (step === 5) {
            title.textContent = t('onboarding.step5Title');
            text.innerHTML = t('onboarding.step5Text');
            this.setFocusHalo('skins-btn');
        } else if (step === 6) {
            title.textContent = t('onboarding.step6Title');
            text.innerHTML = t('onboarding.step6Text');
            this.setFocusHalo('leaderboard-btn');
        } else if (step === 7) {
            title.textContent = t('onboarding.step7Title');
            text.innerHTML = t('onboarding.step7Text');
            this.setFocusHalo('profile-btn');
        } else if (step === 8) {
            title.textContent = t('onboarding.step8Title');
            text.innerHTML = t('onboarding.step8Text');
            this.setFocusHalo('friends-menu-btn');
        } else if (step === 9) {
            const user = this.engine.authManager.currentUser;
            const coins = user ? user.coins : 50;
            const energy = user ? user.energy : 10;
            title.textContent = t('onboarding.step9Title');
            text.innerHTML = t('onboarding.step9Text', { coins, energy });
            btn.textContent = t('onboarding.letsPlay');
        }

        this.container.appendChild(content);

        // Optional: Skip button
        if (step < 9) {
            const skip = document.createElement('div');
            skip.innerHTML = `${t('onboarding.skipToGift')} <span style="display:inline-block;margin-left:5px;vertical-align:middle">${Icons.gift(16)}</span>`;
            skip.style.marginTop = '20px';
            skip.style.fontSize = '0.8rem';
            skip.style.opacity = '0.5';
            skip.style.cursor = 'pointer';
            skip.onclick = () => this.showStep(9);
            this.container.appendChild(skip);
        }
    }

    next() {
        if (this.step < 9) {
            this.showStep(this.step + 1);
        } else {
            this.finish();
        }
    }

    finish() {
        this.container.style.opacity = '0';
        this.removeHalo();
        setTimeout(() => {
            if (this.container.parentElement) this.container.remove();
            this.isActive = false;
            localStorage.setItem('pixelblob_onboarding_done', 'true');
            localStorage.setItem('quicky_onboarding_done', 'true');
            this.engine.showNotification(`${this.engine.i18n.t('onboarding.goodLuck')} ${Icons.rocket(20)}`);
        }, 500);
    }

    // Contextual Tips for Modes
    showContextualTip(mode) {
        const t = this.engine.i18n.t.bind(this.engine.i18n);
        if (this._hasSeenTip(mode)) return;

        // Mark immediately to avoid repeated popups if the user navigates away
        // without pressing the CTA button.
        this._markTipSeen(mode);

        this.isActive = true;
        this.createOverlay();
        this.container.innerHTML = '';

        const content = document.createElement('div');
        content.classList.add('onboarding-content');
        content.style.maxWidth = '400px';
        content.style.animation = 'scaleIn 0.4s ease';

        const title = document.createElement('h2');
        title.style.color = 'var(--secondary-color)';
        title.style.fontSize = '1.8rem';
        title.style.marginBottom = '15px';
        title.style.textShadow = '0 0 10px var(--secondary-color)';

        const text = document.createElement('p');
        text.style.fontSize = '1.1rem';
        text.style.marginBottom = '25px';

        const btn = document.createElement('button');
        btn.classList.add('neon-btn');
        btn.textContent = t('common.gotIt');
        btn.onclick = () => {
            this.container.remove();
            this.isActive = false;
        };

        content.appendChild(title);
        content.appendChild(text);
        content.appendChild(btn);

        if (mode === 'infinite') {
            title.textContent = t('onboarding.tipInfiniteTitle');
            text.innerHTML = t('onboarding.tipInfiniteText');
        } else if (mode === 'challenges') {
            title.textContent = t('onboarding.tipChallengesTitle');
            text.innerHTML = t('onboarding.tipChallengesText');
        } else if (mode === 'all-games') {
            title.textContent = t('onboarding.tipAllGamesTitle');
            text.innerHTML = t('onboarding.tipAllGamesText');
        }

        this.container.appendChild(content);
    }

    attachListeners() {
        const attachTip = (id, mode) => {
            const btn = document.getElementById(id);
            if (!btn) return;
            btn.addEventListener('click', (e) => {
                if (!this._hasSeenTip(mode)) {
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    this.showContextualTip(mode);
                }
            }, true);
        };

        attachTip('infinite-btn', 'infinite');
        attachTip('challenges-btn', 'challenges');
        attachTip('sub-all-games-btn', 'all-games');
    }

    _getTipStorageKeys(mode) {
        const normalizedMode = String(mode || '').replace(/[^a-zA-Z0-9]/g, '_');
        return [
            `pixelblob_tip_${mode}_seen`,
            `pixelblob_tip_${normalizedMode}_seen`,
            `quicky_tip_${mode}_seen`,
            `quicky_tip_${normalizedMode}_seen`
        ];
    }

    _hasSeenTip(mode) {
        return this._getTipStorageKeys(mode).some((key) => localStorage.getItem(key) === 'true');
    }

    _markTipSeen(mode) {
        this._getTipStorageKeys(mode).forEach((key) => localStorage.setItem(key, 'true'));
    }
}
